/**
 * Browser-side glue for rich-text editing in the inline text overlay.
 *
 * The canvas/exporter store rich text as `pluginData.richText.spans` (plain
 * text + character ranges — see shared-util/rich-text). The contentEditable
 * overlay, however, is a live DOM tree the browser mutates as the user types
 * and as execCommand applies bold/italic/etc. These helpers bridge the two:
 *
 *  - spansToOverlayHtml: spans -> styled innerHTML to seed the overlay.
 *  - extractRichText: overlay DOM -> { text, spans } on commit, reading the
 *    effective style off each text node's ancestor chain so it handles whatever
 *    markup execCommand produced (styled spans, <b>/<i>/<u>, <font>, <a>).
 *  - sanitizePastedHtml: clipboard HTML -> overlay markup, by round-tripping
 *    foreign markup through the two above.
 */
import {
  isUnderlined,
  spansForLine,
  splitIntoRuns,
  type ListType,
  type RichTextSpan,
} from '@nexmail/shared-util';

/**
 * Node base style the overlay renders against. Per-span font sizes are seeded
 * as `em` relative to this base so they stay correct under the overlay's zoom
 * and auto-shrink (children scale with the zoomed/shrunk root), and are read
 * back to absolute px on extract via the same base.
 */
export interface OverlayBaseStyle {
  fontSize?: number;
  fontFamily?: string;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Quote a single family name for inline CSS when it contains whitespace. */
function cssFamily(family: string): string {
  const t = family.trim();
  if (/^['"]/.test(t)) return t;
  return /\s/.test(t) ? `'${t}'` : t;
}

/** Primary family from a CSS stack ("'Georgia', serif" -> "Georgia"). */
function primaryFamily(value: string): string {
  return value.split(',')[0].trim().replace(/^["']|["']$/g, '');
}

/**
 * A run's inheritable properties as inline CSS declarations. Decoration is
 * deliberately not here — see runInline.
 */
function runCss(
  style: NonNullable<ReturnType<typeof splitIntoRuns>[number]['style']>,
  base?: OverlayBaseStyle,
): string[] {
  const d: string[] = [];
  if (style.bold) d.push('font-weight:700');
  if (style.italic) d.push('font-style:italic');
  if (style.color) d.push(`color:${style.color}`);
  if (style.fontSize != null && base?.fontSize) {
    // em keeps the run proportional to the (zoomed/auto-shrunk) root font size.
    d.push(`font-size:${Math.round((style.fontSize / base.fontSize) * 1e4) / 1e4}em`);
  }
  if (style.fontFamily) d.push(`font-family:${cssFamily(style.fontFamily)}`);
  return d;
}

/**
 * One run as overlay markup.
 *
 * Underline and strikethrough are emitted as <u>/<s> elements rather than as
 * inline text-decoration, because a CSS decoration propagates to descendants and
 * cannot be cancelled by them: declared on a wrapping span (or on the <a>
 * itself), the underline survives every execCommand the toolbar can issue — the
 * "underline can't be turned off" bug. As an element it is the innermost
 * decoration owner and execCommand('underline') unwraps it natively. Weight,
 * style and color inherit normally, so those stay as CSS.
 *
 * The anchor pins text-decoration:none so that once the <u> is gone the
 * browser's default link underline doesn't silently draw one in its place.
 */
function runInline(run: ReturnType<typeof splitIntoRuns>[number], base?: OverlayBaseStyle): string {
  let body = escapeHtml(run.text);
  if (!run.style) return body;
  if (isUnderlined(run.style)) body = `<u>${body}</u>`;
  if (run.style.strikethrough) body = `<s>${body}</s>`;
  const decls = runCss(run.style, base);
  if (run.style.link) {
    const style = ['color:inherit', 'text-decoration:none', ...decls].join(';');
    return `<a href="${run.style.link.replace(/"/g, '&quot;')}" style="${style}">${body}</a>`;
  }
  return decls.length ? `<span style="${decls.join(';')}">${body}</span>` : body;
}

/** Seed the overlay's innerHTML from spans (+ list) so formatting shows while editing. */
export function spansToOverlayHtml(
  text: string,
  spans: RichTextSpan[],
  list?: ListType | null,
  base?: OverlayBaseStyle,
): string {
  if (list) {
    const tag = list === 'ordered' ? 'ol' : 'ul';
    const lines = text.split('\n');
    let lineBase = 0;
    const items = lines
      .map((line) => {
        const lineSpans = spans.length ? spansForLine(spans, lineBase, line.length) : [];
        lineBase += line.length + 1;
        const body = lineSpans.length
          ? splitIntoRuns(line, lineSpans)
              .map((r) => runInline(r, base))
              .join('')
          : escapeHtml(line) || '<br>';
        return `<li>${body}</li>`;
      })
      .join('');
    return `<${tag} style="margin:0;padding-left:1.4em">${items}</${tag}>`;
  }
  if (!spans.length) return escapeHtml(text);
  return splitIntoRuns(text, spans)
    .map((r) => runInline(r, base))
    .join('');
}

interface Accum {
  text: string;
  // per-character effective style, parallel to `text`
  styles: (RichTextSpan | null)[];
}

/**
 * Parse an overlay inline font-size back to absolute px. Sizes are seeded as
 * `em` relative to the node base, so em/unitless multiply the base; a raw px
 * value is skipped because the overlay is zoomed and the px would be
 * zoom-contaminated (untouched spans keep their em and round-trip cleanly).
 */
function parseOverlayFontSize(value: string, base: number): number | null {
  const m = value.trim().match(/^([\d.]+)(em|rem|px)?$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return null;
  if (m[2] === 'px') return null;
  return Math.round(n * base * 10) / 10;
}

function styleForTextNode(
  node: Node,
  root: HTMLElement,
  base?: OverlayBaseStyle,
): Partial<RichTextSpan> {
  const s: Partial<RichTextSpan> = {};
  let el = node.parentElement;
  while (el && el !== root) {
    const tag = el.tagName;
    const st = el.style;
    const weight = st.fontWeight;
    if (tag === 'B' || tag === 'STRONG' || weight === 'bold' || (weight !== '' && parseInt(weight, 10) >= 600)) {
      s.bold = true;
    }
    if (tag === 'I' || tag === 'EM' || st.fontStyle === 'italic') s.italic = true;
    const deco = `${st.textDecoration} ${st.textDecorationLine}`;
    if (tag === 'U' || deco.includes('underline')) s.underline = true;
    if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL' || deco.includes('line-through')) s.strikethrough = true;
    if (tag === 'A') {
      const href = el.getAttribute('href');
      if (href && s.link == null) s.link = href;
      // The overlay pins text-decoration:none on its own anchors so the UA's
      // link underline can never draw by itself. Reaching one with nothing
      // underlining the text means the user toggled underline off — record that
      // explicitly, because an absent value resolves back to the link default
      // and would switch it on again. A foreign anchor (from a paste) carries no
      // such pin, so it keeps the default. An outer underline still wins: the
      // check above assigns unconditionally, matching how CSS draws it.
      if (s.underline == null && deco.includes('none')) s.underline = false;
    }
    // `color:inherit` is how a seeded anchor defeats the UA's link blue, not a
    // color the span should adopt: recorded, it exports as a literal
    // `color:inherit` and reaches the paragraph builder as an unparseable value.
    if (st.color && st.color !== 'inherit' && s.color == null) s.color = st.color;
    const fontColor = tag === 'FONT' ? el.getAttribute('color') : null;
    if (fontColor && s.color == null) s.color = fontColor;
    // Preserve imported size/family spans the editor doesn't author. Innermost
    // wins; only record a value that actually differs from the node base.
    if (base?.fontSize && s.fontSize == null && st.fontSize) {
      const abs = parseOverlayFontSize(st.fontSize, base.fontSize);
      if (abs != null && Math.abs(abs - base.fontSize) > 0.5) s.fontSize = abs;
    }
    if (s.fontFamily == null && st.fontFamily) {
      const fam = primaryFamily(st.fontFamily);
      if (fam && fam.toLowerCase() !== (base?.fontFamily ?? '').toLowerCase()) s.fontFamily = fam;
    }
    el = el.parentElement;
  }
  return s;
}

function hasOverride(s: Partial<RichTextSpan>): boolean {
  return !!(
    s.bold ||
    s.italic ||
    s.underline ||
    s.strikethrough ||
    s.color ||
    s.link ||
    s.fontSize ||
    s.fontFamily
  );
}

function sameStyle(a: RichTextSpan | null, b: RichTextSpan | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    !!a.bold === !!b.bold &&
    !!a.italic === !!b.italic &&
    !!a.underline === !!b.underline &&
    !!a.strikethrough === !!b.strikethrough &&
    (a.color ?? '') === (b.color ?? '') &&
    (a.link ?? '') === (b.link ?? '') &&
    Math.abs((a.fontSize ?? 0) - (b.fontSize ?? 0)) < 0.5 &&
    (a.fontFamily ?? '') === (b.fontFamily ?? '')
  );
}

/**
 * Extract committed plain text + spans from the live overlay DOM. Walks in
 * document order, treating <br> and block elements (DIV/P) as newlines so the
 * text matches what the renderer lays out (pre-wrap on \n) — equivalent to
 * innerText for the simple markup the overlay produces.
 */
export function extractRichText(
  root: HTMLElement,
  base?: OverlayBaseStyle,
): { text: string; spans: RichTextSpan[]; list: ListType | null } {
  const acc: Accum = { text: '', styles: [] };
  const list: ListType | null = root.querySelector('ol') ? 'ordered' : root.querySelector('ul') ? 'bullet' : null;

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent ?? '';
      if (!t) return;
      const raw = styleForTextNode(node, root, base);
      const style = hasOverride(raw) ? (raw as RichTextSpan) : null;
      for (let i = 0; i < t.length; i++) {
        acc.text += t[i];
        acc.styles.push(style);
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      acc.text += '\n';
      acc.styles.push(null);
      return;
    }
    const isBlock = el.tagName === 'DIV' || el.tagName === 'P' || el.tagName === 'LI';
    if (isBlock && acc.text.length > 0 && !acc.text.endsWith('\n')) {
      acc.text += '\n';
      acc.styles.push(null);
    }
    for (const child of Array.from(el.childNodes)) visit(child);
  };
  for (const child of Array.from(root.childNodes)) visit(child);

  // Coalesce the per-character style array into half-open span ranges.
  const spans: RichTextSpan[] = [];
  let i = 0;
  while (i < acc.styles.length) {
    const style = acc.styles[i];
    if (!style) {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < acc.styles.length && sameStyle(acc.styles[j], style)) j++;
    spans.push({ ...style, start: i, end: j });
    i = j;
  }
  return { text: acc.text, spans, list };
}

/**
 * Normalize clipboard HTML to the overlay's own markup before it is inserted.
 *
 * Left to the browser, a paste drops the source's markup into the overlay
 * verbatim: Word ships mso-* declarations, <style> blocks, class-driven rules
 * and conditional comments, and wraps links in spans that underline them from
 * above — an underline the toolbar then can't remove. Rather than allowlisting
 * tags, run the fragment through the same span model the overlay commits to
 * (extract -> re-seed): whatever the model can't express is dropped by
 * construction, and what survives comes back in canonical form.
 *
 * Size, family and color are dropped so pasted text adopts the node's design
 * rather than importing the source's type scale — which would otherwise land
 * an unloaded family (Calibri) or black body text on a dark section.
 */
export function sanitizePastedHtml(html: string, base?: OverlayBaseStyle): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  // These carry text nodes that aren't content — extractRichText would splice
  // a Word stylesheet in as literal characters.
  parsed.body.querySelectorAll('style,script,noscript,title,meta,link').forEach((el) => el.remove());

  const { text, spans, list } = extractRichText(parsed.body, base);
  if (!text) return '';
  const adopted = spans
    .map(({ color: _color, fontSize: _fontSize, fontFamily: _fontFamily, ...keep }) => keep)
    .filter(hasOverride);
  return spansToOverlayHtml(text, adopted, list, base);
}
